/**
 * Typed client functions for Sickle contracts
 * Uses viem for type-safe contract interactions
 */
import { parseEventLogs } from 'viem';
import { base } from 'viem/chains';
import { addresses } from './addresses.js';
import { SickleFactoryAbi, SickleAbi, SimpleFarmStrategyAbi, FarmStrategyAbi } from './abis.js';
/**
 * Get SickleFactory contract instance
 */
export function getFactory(client, factoryAddress) {
    const address = (factoryAddress || addresses.Factory);
    return {
        address,
        abi: SickleFactoryAbi,
    };
}
/**
 * Get Sickle wallet contract instance
 */
export function getSickle(client, sickleAddress) {
    return {
        address: sickleAddress,
        abi: SickleAbi,
    };
}
/**
 * Get strategy contract instance by name
 */
export function getStrategy(client, strategyName, strategyAddress) {
    const address = (strategyAddress || addresses.Strategies[strategyName]);
    // Return appropriate ABI based on strategy type
    let abi;
    if (strategyName === 'SimpleFarmStrategy') {
        abi = SimpleFarmStrategyAbi;
    }
    else if (strategyName === 'FarmStrategy') {
        abi = FarmStrategyAbi;
    }
    else {
        // For other strategies, we'll need to add their ABIs later
        throw new Error(`Strategy ${strategyName} ABI not yet implemented`);
    }
    return {
        address,
        abi,
    };
}
/**
 * Check if a Sickle wallet exists for an owner
 */
export async function getSickleAddress(client, owner, factoryAddress) {
    const factory = getFactory(client, factoryAddress);
    const sickleAddress = await client.readContract({
        ...factory,
        functionName: 'sickles',
        args: [owner],
    });
    // Return null if address is zero (no wallet deployed)
    if (sickleAddress === '0x0000000000000000000000000000000000000000') {
        return null;
    }
    return sickleAddress;
}
/**
 * Deploy a new Sickle wallet for an owner
 * Requires WalletClient (for signing transactions) and PublicClient (for reading)
 */
export async function deploySickle(walletClient, publicClient, owner, factoryAddress, approved, referralCode) {
    const factory = getFactory(walletClient, factoryAddress);
    // If no approved address provided, use owner (self-approved)
    const approvedAddress = approved || owner;
    // If no referral code provided, use zero bytes32 (64 zeros)
    const referral = referralCode || '0x0000000000000000000000000000000000000000000000000000000000000000';
    const hash = await walletClient.writeContract({
        ...factory,
        functionName: 'deploy',
        args: [approvedAddress, referral],
        account: owner,
        chain: walletClient.chain || base,
    });
    // Wait for transaction to be mined and get receipt
    const receipt = await publicClient.waitForTransactionReceipt({ hash });
    // Try to extract Sickle address from Deploy event in transaction logs
    let sickleAddress = null;
    // Parse Deploy event from logs
    const deployEventAbi = [
        {
            anonymous: false,
            inputs: [
                { indexed: true, internalType: 'address', name: 'owner', type: 'address' },
                { indexed: true, internalType: 'address', name: 'sickle', type: 'address' },
            ],
            name: 'Deploy',
            type: 'event',
        },
    ];
    // Look for Deploy event in logs
    try {
        const deployEvents = parseEventLogs({
            abi: deployEventAbi,
            logs: receipt.logs,
        });
        for (const event of deployEvents) {
            if (event.eventName === 'Deploy' && event.args.owner?.toLowerCase() === owner.toLowerCase()) {
                sickleAddress = event.args.sickle;
                break;
            }
        }
    }
    catch (error) {
        console.warn('Failed to parse Deploy event from logs:', error);
    }
    // Fallback 1: Use predict() function to get deterministic address
    if (!sickleAddress) {
        try {
            const factory = getFactory(publicClient, factoryAddress);
            sickleAddress = await publicClient.readContract({
                ...factory,
                functionName: 'predict',
                args: [owner],
            });
        }
        catch (error) {
            console.warn('Failed to predict Sickle address:', error);
        }
    }
    // Fallback 2: Query factory directly (may need a small delay)
    if (!sickleAddress) {
        // Wait a bit for state to update
        await new Promise(resolve => setTimeout(resolve, 1000));
        sickleAddress = await getSickleAddress(publicClient, owner, factoryAddress);
    }
    if (!sickleAddress || sickleAddress === '0x0000000000000000000000000000000000000000') {
        throw new Error('Failed to deploy Sickle wallet - could not determine address');
    }
    return sickleAddress;
}
/**
 * Build a multicall transaction for Sickle
 */
export function buildMulticall(calls) {
    return {
        to: addresses.Factory, // This will be the Sickle address in actual usage
        data: '0x',
        functionName: 'multicall',
        args: [calls],
    };
}
